<template>
    <div
        class="appv2"
        :class="{
            'appv2--collapsed': isSidebarCollapsed,
            'appv2--mobile-open': isMobileMenuOpen
        }"
        data-testid="app-layout-v2"
    >
        <aside class="appv2-sidebar" data-testid="app-layout-v2-sidebar">
            <div class="appv2-sidebar-header">
                <router-link
                    v-if="logoSettings !== LOGO_SETTINGS.NO_LOGO && (!isSidebarCollapsed || isMobileMenuOpen)"
                    to="/"
                    class="appv2-sidebar-brand-wrap"
                >
                    <img :src="logoSrc" alt="Logo" class="appv2-sidebar-logo-img" />
                </router-link>
                <button
                    type="button"
                    class="appv2-sidebar-toggle"
                    :aria-label="isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
                    :title="isSidebarCollapsed ? 'Expand' : 'Collapse'"
                    @click="onToggleClick"
                >
                    <i :class="toggleIcon"></i>
                </button>
            </div>

            <div class="appv2-sidebar-body">
                <slot name="sidebar">
                    <div class="appv2-placeholder">
                        <i class="fal fa-bars"></i>
                        <span v-show="!isSidebarCollapsed || isMobileMenuOpen">Sidebar V2</span>
                    </div>
                </slot>
            </div>

            <div
                v-if="getIsUnderImpersonation"
                class="appv2-impersonate"
                :class="{ 'appv2-impersonate--collapsed': isSidebarCollapsed && !isMobileMenuOpen }"
                data-testid="app-layout-v2-impersonate"
            >
                <div v-show="!isSidebarCollapsed || isMobileMenuOpen" class="appv2-impersonate-info">
                    <div class="appv2-impersonate-header">
                        <i class="fal fa-user-secret"></i>
                        <span class="appv2-impersonate-label">{{ $t('impersonation.impersonated') }}</span>
                    </div>
                    <div class="appv2-impersonate-user">
                        <strong>{{ getUserImpersonated ? getUserImpersonated.name : '' }}</strong>
                        <span
                            v-if="getUserImpersonated && getUserImpersonated.company"
                            class="appv2-impersonate-company"
                        >
                            {{ getUserImpersonated.company.name }}
                        </span>
                    </div>
                </div>
                <button
                    type="button"
                    class="appv2-impersonate-leave"
                    :title="$t('impersonation.leave')"
                    @click="onLeaveImpersonation"
                >
                    <i class="fal fa-sign-out"></i>
                    <span v-show="!isSidebarCollapsed || isMobileMenuOpen">{{ $t('impersonation.leave') }}</span>
                </button>
            </div>

            <div class="appv2-sidebar-footer" data-testid="app-layout-v2-sidebar-footer">
                <span
                    v-show="!isSidebarCollapsed || isMobileMenuOpen"
                    class="appv2-status-pill"
                    :class="`appv2-status-pill--${systemStatus.tone}`"
                    :title="systemStatus.label"
                >
                    <span class="appv2-status-dot"></span>
                    {{ systemStatus.label }}
                </span>
                <span class="appv2-sidebar-version">v{{ appVersion }}</span>
            </div>
        </aside>

        <div
            v-if="isMobileMenuOpen"
            class="appv2-backdrop"
            aria-hidden="true"
            @click="closeMobileMenu"
        ></div>

        <header class="appv2-topbar" data-testid="app-layout-v2-topbar">
            <button
                type="button"
                class="appv2-burger"
                aria-label="Open menu"
                @click="toggleMobileMenu"
            >
                <i class="fal fa-bars"></i>
            </button>
            <div class="appv2-topbar-slot">
                <slot name="topbar">
                    <div class="appv2-placeholder appv2-placeholder--topbar">
                        <i class="fal fa-ellipsis-h"></i>
                        <span>Topbar V2</span>
                    </div>
                </slot>
            </div>
        </header>

        <main class="appv2-main" data-testid="app-layout-v2-main">
            <slot />
        </main>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapState, mapActions, mapGetters } from 'vuex'
import pkg from '../../../../package.json'
import LOGO_SETTINGS from '@/helpers/logoSettings.js'
import { fetchSystemHealth } from '@/services/SystemHealthService'

const MOBILE_BREAKPOINT = 900
const DEFAULT_LOGO = '/img/logo-1.png'
const HEALTH_POLL_INTERVAL = 60 * 1000

export default {
    name: 'AppLayoutV2',
    data() {
        return {
            LOGO_SETTINGS,
            appVersion: pkg.version,
            systemStatus: { tone: 'ok', label: 'Opérationnel' },
            healthTimer: null
        }
    },
    computed: {
        ...mapState('ui', ['isSidebarCollapsed', 'isMobileMenuOpen']),
        ...mapState('company', { companySettings: (s) => s.companySettings }),
        ...mapState({ baseURL: (s) => s.baseURL }),
        ...mapState('auth', { token: (s) => s.token }),
        ...mapGetters('admin', ['getIsUnderImpersonation', 'getUserImpersonated']),
        logoSettings() {
            return this.companySettings && this.companySettings.logo_settings
                ? this.companySettings.logo_settings
                : LOGO_SETTINGS.DEFAULT_LOGO
        },
        logoSrc() {
            if (this.logoSettings === LOGO_SETTINGS.CUSTOM_LOGO && this.companySettings) {
                const current = (this.companySettings.settings_logo || []).find((item) => item.is_logo)
                if (current) {
                    return `${this.baseURL}/storage/logo/${current.id}?token=${this.token}`
                }
            }
            return DEFAULT_LOGO
        },
        toggleIcon() {
            if (this.isMobileMenuOpen) return 'fal fa-times'
            return this.isSidebarCollapsed ? 'fal fa-angle-double-right' : 'fal fa-angle-double-left'
        }
    },
    watch: {
        $route() {
            if (this.isMobileMenuOpen) this.closeMobileMenu()
        },
        isMobileMenuOpen(open) {
            if (typeof document === 'undefined') return
            document.body.classList.toggle('appv2-no-scroll', open)
        }
    },
    mounted() {
        document.addEventListener('keydown', this.onKey)
        if (!this.companySettings) {
            // Monté sur chaque page et pour chaque rôle : un refus doit rester
            // sans effet visible, pas renvoyer l'utilisateur sur /403.
            this.getCompanySettings({ silence403: true }).catch(() => {})
        }
        this.checkHealth()
        this.healthTimer = setInterval(this.checkHealth, HEALTH_POLL_INTERVAL)
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.onKey)
        document.body.classList.remove('appv2-no-scroll')
        if (this.healthTimer) {
            clearInterval(this.healthTimer)
            this.healthTimer = null
        }
    },
    methods: {
        ...mapActions('ui', ['toggleSidebar', 'closeMobileMenu', 'toggleMobileMenu']),
        ...mapActions('admin', ['leaveImpersonate']),
        ...mapActions('company', ['getCompanySettings']),
        onToggleClick() {
            if (this.isMobileMenuOpen) {
                this.closeMobileMenu()
            } else {
                this.toggleSidebar()
            }
        },
        onKey(e) {
            if (e.key === 'Escape' && this.isMobileMenuOpen) this.closeMobileMenu()
        },
        isMobile() {
            if (typeof window === 'undefined') return false
            return window.innerWidth <= MOBILE_BREAKPOINT
        },
        onLeaveImpersonation() {
            this.leaveImpersonate().then(() => {
                window.location.href = '/'
            })
        },
        checkHealth() {
            fetchSystemHealth()
                .then(({ data }) => {
                    const status = data && typeof data === 'object' ? data.status : null
                    if (status === 'degraded') {
                        this.systemStatus = { tone: 'warn', label: 'Dégradé' }
                    } else {
                        this.systemStatus = { tone: 'ok', label: 'Opérationnel' }
                    }
                })
                .catch(() => {
                    this.systemStatus = { tone: 'err', label: 'Hors ligne' }
                })
        }
    }
}
</script>

<style>
/* Lock body scroll while drawer is open (global intentionally) */
body.appv2-no-scroll {
    overflow: hidden;
}
</style>

<style scoped>
.appv2 {
    display: grid;
    grid-template-columns: var(--v2-sidebar-width, 240px) 1fr;
    grid-template-rows: var(--v2-topbar-height, 56px) 1fr;
    grid-template-areas:
        'sidebar topbar'
        'sidebar main';
    min-height: 100vh;
    background: var(--v2-surface-1);
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
    transition: grid-template-columns var(--v2-transition-base);
}

.appv2--collapsed {
    grid-template-columns: var(--v2-sidebar-collapsed-width, 64px) 1fr;
}

.appv2-sidebar {
    grid-area: sidebar;
    background: var(--v2-surface-0);
    border-right: 1px solid var(--v2-border);
    display: flex;
    flex-direction: column;
    position: sticky;
    top: 0;
    height: 100vh;
    /* Explicit z-index above Leaflet's default .leaflet-top/.leaflet-bottom
     * (1000) so the collapsed-state flyouts rendered inside this stacking
     * context aren't obscured by a map sitting in .appv2-main. Stays well
     * below BsModal (1050), BmvModal (9999), BmvDialog (10000) and the
     * toast container (11000), so overlays still cover the sidebar. */
    z-index: 1010;
    /* Soft shadow cast toward the main content to give a lifted feel —
     * makes it obvious the sidebar layers above .appv2-main when users
     * scroll the page. */
    box-shadow:
        2px 0 6px rgba(15, 23, 42, 0.04),
        8px 0 24px rgba(15, 23, 42, 0.06);
    /* overflow: visible so collapsed-state tooltips from children can escape to the right */
}

.appv2-sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-2);
    height: var(--v2-topbar-height, 56px);
    padding: 0 var(--v2-space-4);
    border-bottom: 1px solid var(--v2-border-subtle);
    flex-shrink: 0;
}

.appv2-sidebar-brand-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    text-decoration: none !important;
    min-width: 0;
    flex: 1;
    height: 100%;
}

.appv2-sidebar-logo-img {
    height: 36px;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    display: block;
}

.appv2-sidebar-toggle {
    width: 28px;
    height: 28px;
    border-radius: var(--v2-radius-sm);
    border: none;
    background: transparent;
    color: var(--v2-text-3);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background var(--v2-transition-fast),
        color var(--v2-transition-fast);
    flex-shrink: 0;
}

.appv2-sidebar-toggle:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.appv2-sidebar-toggle:focus-visible {
    outline: none;
    box-shadow: var(--v2-shadow-focus);
}

.appv2--collapsed .appv2-sidebar-header {
    justify-content: center;
    padding: 0 var(--v2-space-2);
}

.appv2-sidebar-body {
    flex: 1;
    min-height: 0;
    /* Expanded: the nav scrolls inside the body so it never runs under the
     * footer on short viewports. */
    overflow-y: auto;
    overflow-x: hidden;
}

/* Collapsed: overflow stays visible so the flyouts can escape to the right
 * (overflow-y: auto would clip them). If the icon-only nav ever gets tall
 * enough to exceed the viewport, switch to a portal-based flyout in MenuV2. */
.appv2--collapsed .appv2-sidebar-body {
    overflow: visible;
}

.appv2-topbar {
    grid-area: topbar;
    background: var(--v2-surface-0);
    border-bottom: 1px solid var(--v2-border);
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
    display: flex;
    align-items: center;
    padding: 0 var(--v2-space-5);
    position: sticky;
    top: 0;
    z-index: 20;
    gap: var(--v2-space-2);
}

.appv2-topbar-slot {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: stretch;
}

/* Burger — hidden on desktop, visible on mobile */
.appv2-burger {
    display: none;
    width: 36px;
    height: 36px;
    border-radius: var(--v2-radius-sm);
    border: none;
    background: transparent;
    color: var(--v2-text-2);
    cursor: pointer;
    font-size: 1.1rem;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.appv2-burger:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.appv2-burger:focus-visible {
    outline: none;
    box-shadow: var(--v2-shadow-focus);
}

.appv2-main {
    grid-area: main;
    padding: 0;
    overflow-x: hidden;
}

.appv2-impersonate {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 12px var(--v2-space-4);
    border-top: 1px solid rgba(245, 158, 11, 0.35);
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(217, 119, 6, 0.12));
    color: #b45309;
    font-size: 0.72rem;
    line-height: 1.35;
}

.appv2-impersonate--collapsed {
    flex-direction: row;
    justify-content: center;
    padding: 10px 0;
}

.appv2-impersonate-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.appv2-impersonate-header {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.65rem;
    font-weight: var(--v2-font-weight-bold);
    color: #b45309;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.appv2-impersonate-header i {
    font-size: 0.9rem;
    color: #d97706;
    flex-shrink: 0;
}

.appv2-impersonate-label {
    white-space: normal;
    word-break: break-word;
}

.appv2-impersonate-user {
    display: flex;
    flex-direction: column;
    gap: 2px;
    color: var(--v2-text-1);
    font-size: 0.78rem;
    word-break: break-word;
}

.appv2-impersonate-user strong {
    font-weight: var(--v2-font-weight-bold);
    line-height: 1.25;
}

.appv2-impersonate-company {
    font-size: 0.7rem;
    color: var(--v2-text-3);
    font-weight: var(--v2-font-weight-medium);
    line-height: 1.3;
}

.appv2-impersonate-leave {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 30px;
    padding: 0 12px;
    background: rgba(217, 119, 6, 0.15);
    color: #b45309;
    border: 1px solid rgba(217, 119, 6, 0.4);
    border-radius: var(--v2-radius-sm);
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    font-family: inherit;
    width: 100%;
}

.appv2-impersonate-leave:hover {
    background: rgba(217, 119, 6, 0.25);
    border-color: rgba(217, 119, 6, 0.6);
}

.appv2-impersonate-leave i {
    font-size: 0.8rem;
    color: #b45309;
}

.appv2-impersonate--collapsed .appv2-impersonate-leave {
    padding: 0;
    width: 30px;
    height: 30px;
}

.appv2-sidebar-footer {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-2);
    padding: var(--v2-space-2) var(--v2-space-4);
    border-top: 1px solid var(--v2-border-subtle);
    background: var(--v2-surface-1);
    color: var(--v2-text-3);
    font-size: 0.7rem;
    line-height: 1;
    user-select: none;
}

.appv2--collapsed .appv2-sidebar-footer {
    justify-content: center;
    padding: var(--v2-space-2) 0;
}

.appv2-sidebar-version {
    color: var(--v2-text-4);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
    font-weight: var(--v2-font-weight-medium);
}

.appv2-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 0;
    color: var(--v2-text-2);
    font-weight: var(--v2-font-weight-medium);
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.appv2-status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--v2-success);
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.12);
    position: relative;
    flex-shrink: 0;
}

.appv2-status-dot::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: var(--v2-success);
    opacity: 0.55;
    animation: appv2StatusPulse 2.2s ease-out infinite;
}

.appv2-status-pill--warn .appv2-status-dot {
    background: var(--v2-warning);
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.14);
}

.appv2-status-pill--warn .appv2-status-dot::after {
    background: var(--v2-warning);
}

.appv2-status-pill--err .appv2-status-dot {
    background: var(--v2-danger);
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.14);
}

.appv2-status-pill--err .appv2-status-dot::after {
    background: var(--v2-danger);
}

@keyframes appv2StatusPulse {
    0%   { transform: scale(1);   opacity: 0.55; }
    70%  { transform: scale(2.2); opacity: 0; }
    100% { transform: scale(2.2); opacity: 0; }
}

/* Mobile drawer overlay (under topbar, behind sidebar) */
.appv2-backdrop {
    display: none;
}

.appv2-placeholder {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    padding: var(--v2-space-4);
    color: var(--v2-text-4);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-medium);
    letter-spacing: 0.02em;
    text-transform: uppercase;
}

.appv2--collapsed .appv2-placeholder {
    justify-content: center;
    padding: var(--v2-space-4) 0;
}

.appv2-placeholder--topbar {
    padding: 0;
}

@media (max-width: 900px) {
    .appv2 {
        grid-template-columns: 1fr;
        grid-template-rows: var(--v2-topbar-height, 56px) 1fr;
        grid-template-areas:
            'topbar'
            'main';
        transition: none;
    }

    .appv2-burger {
        display: inline-flex;
    }

    /* Topbar stays pinned to the top of the viewport on mobile */
    .appv2-topbar {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 50;
    }

    .appv2-main {
        padding-top: var(--v2-topbar-height, 56px);
    }

    /* Sidebar becomes a slide-in drawer */
    .appv2-sidebar {
        position: fixed;
        left: 0;
        top: 0;
        bottom: 0;
        width: var(--v2-sidebar-width, 260px);
        z-index: 60;
        transform: translateX(-100%);
        transition: transform var(--v2-transition-base);
        box-shadow: var(--v2-shadow-lg);
    }

    .appv2--mobile-open .appv2-sidebar {
        transform: translateX(0);
    }

    /* Collapse state doesn't apply when sidebar is a drawer on mobile */
    .appv2--collapsed .appv2-sidebar {
        width: var(--v2-sidebar-width, 260px);
    }

    /* The drawer shows full labels, so the nav must scroll like the expanded desktop sidebar */
    .appv2--collapsed .appv2-sidebar-body {
        overflow-y: auto;
        overflow-x: hidden;
    }

    .appv2--collapsed .appv2-sidebar-header {
        justify-content: space-between;
        padding: 0 var(--v2-space-4);
    }

    .appv2--collapsed .appv2-placeholder {
        justify-content: flex-start;
        padding: var(--v2-space-4);
    }

    .appv2-backdrop {
        display: none;
    }

    .appv2--mobile-open .appv2-backdrop {
        display: block;
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.35);
        z-index: 55;
        backdrop-filter: blur(2px);
    }
}

</style>
